import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from 'store'
import {
  approveRequest,
  cancelRequest,
  getRequests,
  getRequestsMe,
  rejectRequest,
} from 'features/requests/requestsSlice'
import { setConfirmModal } from 'features/global/globalSlice'
import { ModalTypes } from 'types/global.interface'
import { IRequestRecord } from 'types/requests.interface'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { PageWrapper, Flex, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { Head1 } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { RequestsBoard } from './Sections/RequestsBoard'
import { RequestDrawer } from './Sections/RequestDrawer'
import { NewRequestModal } from './Sections/NewRequestModal'
import { HeaderRight, SegmentBar } from './Requests.styled'

export const Requests = () => {
  const dispatch = useDispatch<AppDispatch>()
  const { currentUser } = useSelector((state: RootState) => state.requests)
  const [segment, setSegment] = useState<'approvals' | 'mine'>('approvals')
  const [selected, setSelected] = useState<IRequestRecord | null>(null)
  const [modalOpen, setModalOpen] = useState(false)

  useEffect(() => {
    dispatch(getRequestsMe())
  }, [dispatch])

  const canApprove = !!currentUser?.canApprove
  const activeSegment = canApprove ? segment : 'mine'

  const reload = (scope: 'approvals' | 'mine') => dispatch(getRequests({ scope, limit: 200 }))

  const confirm = (word: string, run: () => Promise<unknown>) => {
    setSelected(null)
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: async () => {
          await run()
          await reload(activeSegment)
        },
        setParams: { type: ModalTypes.BeforeSabe },
        word,
      }),
    )
  }

  const handleApprove = (note: string) => {
    if (!selected) return
    const id = selected.id
    confirm('approve this request', () => dispatch(approveRequest({ id, note })))
  }

  const handleReject = (note: string) => {
    if (!selected) return
    const id = selected.id
    confirm('reject this request', () => dispatch(rejectRequest({ id, note })))
  }

  const handleQuickApprove = (req: IRequestRecord) => {
    confirm('approve this request', () => dispatch(approveRequest({ id: req.id, note: '' })))
  }

  const handleCancel = (req: IRequestRecord) => {
    confirm('cancel this request', () => dispatch(cancelRequest(req.id)))
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16' style={{ minHeight: '2.5rem' }}>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Requests
          </Head1>
          <HeaderRight>
            <Button variant='primary' onClick={() => setModalOpen(true)}>
              New Request
            </Button>
          </HeaderRight>
        </Flex>

        <SegmentBar>
          <TabsContainer>
            {canApprove && (
              <Tabs active={activeSegment === 'approvals'} onClick={() => setSegment('approvals')}>
                Approvals
              </Tabs>
            )}
            <Tabs active={activeSegment === 'mine'} onClick={() => setSegment('mine')}>
              My Requests
            </Tabs>
          </TabsContainer>
        </SegmentBar>

        <RequestsBoard
          scope={activeSegment}
          canApprove={canApprove}
          onSelect={setSelected}
          onQuickApprove={handleQuickApprove}
          onCancel={handleCancel}
        />
      </PageWrapper>

      {selected && (
        <RequestDrawer
          req={selected}
          scope={activeSegment}
          canApprove={canApprove}
          currentUserId={currentUser?.id ?? null}
          onClose={() => setSelected(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onCancel={() => handleCancel(selected)}
        />
      )}
      {modalOpen && (
        <NewRequestModal
          closeModal={async (submitted?: boolean, forSelf?: boolean) => {
            setModalOpen(false)
            if (!submitted) return
            const nextScope: 'approvals' | 'mine' =
              forSelf || !canApprove ? 'mine' : 'approvals'
            setSegment(nextScope)
            await reload(nextScope)
          }}
        />
      )}
    </>
  )
}
